import { UserRequest } from '../dtos/userRequest';
import {
  MAX_WEATHER_REQUEST_IDS,
  ToneResponseWeather,
  ToneResponseWeatherResponse,
} from '../dtos/weather';
import { ApiResponseError } from '../dtos/common';
import { accessToken } from '../utils/helper';
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../utils/api';

const getApiErrorMessage = (error: unknown): string => {
  const apiError = error as ApiResponseError;

  return apiError.response?.data?.message || 'Failed to get tone response weather';
};

import { ElevationData } from '../dtos/elevation';

interface LocationState {
  userRequestList:
    | { data: UserRequest[]; total: number; totalPages: number }
    | { data: []; total: 0; totalPages: 0 };
  deviceCodeList:
    | { data: UserRequest[]; total: number; totalPages: number }
    | { data: []; total: 0; totalPages: 0 };
  /** Weather snapshots keyed by UserRequest.id; ids with no capture stay absent. */
  weatherByRequestId: Record<number, ToneResponseWeather>;
  weatherLoading: boolean;
  weatherError: string | null;
  /** Elevation snapshots keyed by UserRequest.id */
  elevationByRequestId: Record<number, ElevationData>;
  elevationLoading: boolean;
  elevationError: string | null;
  loading: boolean;
  error: string | null;
}

const initialState: LocationState = {
  userRequestList: { data: [], total: 0, totalPages: 0 },
  deviceCodeList: { data: [], total: 0, totalPages: 0 },
  weatherByRequestId: {},
  weatherLoading: false,
  weatherError: null,
  elevationByRequestId: {},
  elevationLoading: false,
  elevationError: null,
  loading: false,
  error: null,
};

export const getAllUserRequestTrigger = createAsyncThunk(
  'get/user-request',
  async ({
    page,
    limit,
    search,
    sortOrder,
    sortField,
  }: {
    page?: number;
    limit?: number;
    search?: string;
    sortOrder?: string;
    sortField?: string;
  }) => {
    try {
      // Clean the sortField to remove any malformed characters - use snake_case to match iOS app
      const cleanSortField = sortField ? sortField.split(':')[0] : undefined;

      const queryParams = new URLSearchParams();
      if (page) queryParams.append('page', page.toString());
      if (limit) queryParams.append('limit', limit.toString());
      if (search) queryParams.append('search', search);
      if (sortOrder) queryParams.append('sortOrder', sortOrder);
      if (cleanSortField) queryParams.append('sortField', cleanSortField);

      const url = `/user-request?${queryParams}`;
      console.log('🔍 [userRequestSlice] Final URL:', url);

      const response = await api.get(url, accessToken());
      return response.data;
    } catch (error) {
      console.log(error);
      return { data: [], totalPages: 0 };
    }
  },
);

export const getDeviceCodeTrigger = createAsyncThunk(
  'get/user-request/device-code',
  async ({
    page,
    limit,
    search,
    deviceCode,
  }: {
    page?: number;
    limit?: number;
    search?: string;
    deviceCode: string;
  }) => {
    try {
      const queryParams = new URLSearchParams();
      if (page) queryParams.append('page', page.toString());
      if (limit) queryParams.append('limit', limit.toString());
      if (search) queryParams.append('search', search);
      if (deviceCode) queryParams.append('deviceCode', deviceCode);

      const response = await api.get(`/user-request/device-code?${queryParams}`, accessToken());
      return response.data;
    } catch (error) {
      console.log(error);
      return { data: [], totalPages: 0 };
    }
  },
);

/**
 * Fetches the weather captured for a page of tone responses in a single call.
 * The backend caps a batch at MAX_WEATHER_REQUEST_IDS, so ids are chunked.
 */
export const getToneResponseWeatherTrigger = createAsyncThunk(
  'get/user-request/weather',
  async ({ requestIds }: { requestIds: number[] }, { rejectWithValue }) => {
    try {
      const uniqueIds = Array.from(
        new Set(requestIds.filter((requestId) => Number.isFinite(requestId))),
      );

      if (uniqueIds.length === 0) {
        return { data: [] } as ToneResponseWeatherResponse;
      }

      const chunks: number[][] = [];
      for (let index = 0; index < uniqueIds.length; index += MAX_WEATHER_REQUEST_IDS) {
        chunks.push(uniqueIds.slice(index, index + MAX_WEATHER_REQUEST_IDS));
      }

      const responses = await Promise.all(
        chunks.map((chunk) =>
          api.get(`/user-request/weather?requestIds=${chunk.join(',')}`, accessToken()),
        ),
      );

      return {
        data: responses.flatMap((response) => response.data?.data || []),
      } as ToneResponseWeatherResponse;
    } catch (error) {
      return rejectWithValue(getApiErrorMessage(error));
    }
  },
);

/**
 * Fetches elevation for a page of tone response coordinates in batched GET calls.
 */
export const getToneResponseElevationTrigger = createAsyncThunk(
  'get/user-request/elevation',
  async (
    {
      items,
    }: {
      items: Array<{
        id: number;
        latitude?: string | number | null;
        longitude?: string | number | null;
      }>;
    },
    { rejectWithValue },
  ) => {
    try {
      const validItems = items.filter((item) => {
        if (!item.latitude || !item.longitude) return false;
        const lat =
          typeof item.latitude === 'string'
            ? Number.parseFloat(item.latitude)
            : item.latitude;
        const lon =
          typeof item.longitude === 'string'
            ? Number.parseFloat(item.longitude)
            : item.longitude;
        return (
          Number.isFinite(lat) &&
          Number.isFinite(lon) &&
          !(lat === 0 && lon === 0) &&
          lat >= -90 &&
          lat <= 90 &&
          lon >= -180 &&
          lon <= 180
        );
      });

      if (validItems.length === 0) {
        return {
          data: [] as Array<{ requestId: number; elevation: ElevationData }>,
        };
      }

      const CHUNK_SIZE = 50;
      const chunks: Array<typeof validItems> = [];
      for (let i = 0; i < validItems.length; i += CHUNK_SIZE) {
        chunks.push(validItems.slice(i, i + CHUNK_SIZE));
      }

      const results: Array<{ requestId: number; elevation: ElevationData }> = [];

      for (const chunk of chunks) {
        const latitudes = chunk.map((item) => item.latitude).join(',');
        const longitudes = chunk.map((item) => item.longitude).join(',');

        const response = await api.get(
          `/elevation?latitude=${latitudes}&longitude=${longitudes}`,
          accessToken(),
        );

        const data = response.data?.data;
        if (Array.isArray(data)) {
          chunk.forEach((item, index) => {
            const elev = data[index];
            if (elev) {
              results.push({ requestId: item.id, elevation: elev });
            }
          });
        } else if (data && chunk.length === 1) {
          results.push({ requestId: chunk[0].id, elevation: data });
        }
      }

      return { data: results };
    } catch (error) {
      return rejectWithValue(getApiErrorMessage(error));
    }
  },
);

const userRequestSlice = createSlice({
  name: 'location',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(getAllUserRequestTrigger.pending, (state) => {
        state.loading = true;
      })
      .addCase(getAllUserRequestTrigger.fulfilled, (state, action) => {
        state.loading = false;
        state.userRequestList = action.payload;
      })
      .addCase(getAllUserRequestTrigger.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to get user request';
      })
      .addCase(getDeviceCodeTrigger.pending, (state) => {
        state.loading = true;
      })
      .addCase(getDeviceCodeTrigger.fulfilled, (state, action) => {
        state.loading = false;
        state.deviceCodeList = action.payload;
      })
      .addCase(getDeviceCodeTrigger.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to get device code';
      })
      .addCase(getToneResponseWeatherTrigger.pending, (state) => {
        state.weatherLoading = true;
        state.weatherError = null;
      })
      .addCase(getToneResponseWeatherTrigger.fulfilled, (state, action) => {
        state.weatherLoading = false;
        // Merge rather than replace so cached pages survive pagination.
        state.weatherByRequestId = action.payload.data.reduce(
          (accumulator, weather) => ({
            ...accumulator,
            [weather.requestId]: weather,
          }),
          { ...state.weatherByRequestId },
        );
      })
      .addCase(getToneResponseWeatherTrigger.rejected, (state, action) => {
        state.weatherLoading = false;
        state.weatherError = (action.payload as string) || 'Failed to get tone response weather';
      })
      .addCase(getToneResponseElevationTrigger.pending, (state) => {
        state.elevationLoading = true;
        state.elevationError = null;
      })
      .addCase(getToneResponseElevationTrigger.fulfilled, (state, action) => {
        state.elevationLoading = false;
        state.elevationByRequestId = action.payload.data.reduce(
          (accumulator, item) => ({
            ...accumulator,
            [item.requestId]: item.elevation,
          }),
          { ...state.elevationByRequestId },
        );
      })
      .addCase(getToneResponseElevationTrigger.rejected, (state, action) => {
        state.elevationLoading = false;
        state.elevationError = (action.payload as string) || 'Failed to get elevation';
      });
  },
});
export default userRequestSlice.reducer;
